import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Calendar, User, Mail, Phone, Award, 
  DollarSign, Clock, FileText, ExternalLink, 
  ShieldCheck, MapPin, Building, ChevronRight, AlertTriangle, 
  Video
} from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../../common/apiClient';
import SummaryApi from '../../common/SummaryApi';
import PageLoader from '../../helpers/PageLoader';

const OfferDetailView = ({ id: propId, onBack }) => {
  const { id: urlId } = useParams();
  const id = propId || urlId; // Supports both router URL params and prop injection
  const navigate = useNavigate();

  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch single offer details populated with candidate metadata
  const fetchOfferDetails = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(SummaryApi.getCandidateOfferById.url(id));
      if (response.data.success) {
        setOffer(response.data.offer);
      }
    } catch (err) {
      toast.error('Failed to load candidate offer specifications');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchOfferDetails();
  }, [fetchOfferDetails]);

  // Currency Formatter Utility
  const formatCurrency = (val) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(val || 0);
  };

  // Date Formatter Utilities
  const formatDate = (isoString) => {
    if (!isoString) return 'N/A';
    return new Date(isoString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return 'N/A';
    return new Date(isoString).toLocaleString('en-IN', {
      dateStyle: 'medium',
      timeStyle: 'short'
    });
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'draft': return 'bg-gray-100 text-gray-700 border-gray-200';
      case 'sent': return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'accepted': return 'bg-green-50 text-green-700 border-green-200';
      case 'rejected': return 'bg-red-50 text-red-700 border-red-200';
      case 'withdrawn': return 'bg-amber-50 text-amber-700 border-amber-200';
      default: return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  };

  const handleBackNavigation = () => {
    if (onBack) {
      onBack();
    } else {
      navigate('/recruitment/offers'); // Fallback fallback route
    }
  };

  if (loading) return <PageLoader />;
  if (!offer) return (
    <div className="p-8 text-center bg-white border rounded-xl max-w-md mx-auto mt-12 space-y-3">
      <AlertTriangle className="h-10 w-10 text-amber-500 mx-auto" />
      <h3 className="font-bold text-gray-800 text-sm">Offer Package Not Found</h3>
      <p className="text-xs text-gray-400">The requested candidate offer contract could not be loaded.</p>
      <button onClick={handleBackNavigation} className="text-xs font-bold text-primary hover:underline">Go Back</button>
    </div>
  );

  const candidate = offer.candidateId || {};
  const roleName = offer.designationId?.name || offer.designationId?.title || "Assigned Position";
  const deptName = offer.departmentId?.name || offer.departmentId?.departmentName || "N/A";

  return (
    <div className="space-y-6">
      
      {/* Top Navigation Control bar */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-4 md:p-6 rounded-xl border border-gray-100 shadow-sm">
        <div className="flex items-center gap-3">
          <button 
            onClick={handleBackNavigation} 
            className="p-2.5 rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-700 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-base sm:text-lg font-bold text-gray-900 leading-tight">
                Offer Specifications for {candidate.firstName} {candidate.lastName}
              </h1>
              <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-extrabold uppercase border ${getStatusBadgeClass(offer.status)}`}>
                {offer.status}
              </span>
            </div>
            <p className="text-[11px] text-gray-400 mt-1">
              Package ID: <span className="font-semibold text-gray-700">{offer.offerId || 'PENDING'}</span> &bull; Applied For: <span className="font-semibold text-gray-700">{offer.jobOpeningId?.title || 'N/A'}</span>
            </p>
          </div>
        </div>
      </div>

      {/* Main Grid Workspace split into 3-columns */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Left Side: Main Tables and Evaluations logs (Takes 2/3 width) */}
        <div className="lg:col-span-2 space-y-6">
          
          {/* 👈 Table 1: Modern Salary Breakup table */}
          <div className="bg-white border border-gray-100 rounded-xl shadow-sm p-5 space-y-4">
            <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-wider border-b pb-2 flex items-center gap-1.5">
              <DollarSign className="h-4 w-4 text-gray-400" /> Compensation Structure Table
            </h3>
            
            {offer.salaryBreakup?.length > 0 ? (
              <div className="border border-gray-100 rounded-lg overflow-hidden bg-white">
                <table className="w-full text-left border-collapse text-xs">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100 text-gray-400 font-bold text-[9px] uppercase">
                      <th className="p-3">Salary Component</th>
                      <th className="p-3">Frequency</th>
                      <th className="p-3 text-right">Amount (Annual)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 text-gray-700">
                    {offer.salaryBreakup.map((comp, idx) => (
                      <tr key={idx} className="hover:bg-gray-50/50 transition-colors">
                        <td className="p-3 font-semibold text-gray-800">{comp.component}</td>
                        <td className="p-3 font-semibold text-gray-400 capitalize">{comp.frequency || 'Annual'}</td>
                        <td className="p-3 text-right font-bold text-gray-900">{formatCurrency(comp.amount)}</td>
                      </tr>
                    ))}
                    {/* Sum total row */}
                    <tr className="bg-gray-50 font-bold text-gray-900">
                      <td className="p-3">Total Compensation Value (CTC)</td>
                      <td className="p-3 text-gray-400 uppercase">{offer.currency || 'INR'}</td>
                      <td className="p-3 text-right text-primary text-sm">{formatCurrency(offer.ctc)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-4 bg-gray-50 rounded-lg text-center text-xs text-gray-400 italic">
                No component breakup configured. Compensation structures are mapped as a single cumulative package.
              </div>
            )}
          </div>

          {/* 👈 Table 2: Modern Candidate Recruitment Trail (Stage History Timeline) */}
          <div className="bg-white border border-gray-100 rounded-xl shadow-sm p-5 space-y-4">
            <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-wider border-b pb-2 flex items-center gap-1.5">
              <Clock className="h-4 w-4 text-gray-400" /> Recruitment Trail Log Table
            </h3>

            {candidate.stageHistory?.length > 0 ? (
              <div className="border border-gray-100 rounded-lg overflow-hidden bg-white">
                <table className="w-full text-left border-collapse text-xs">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100 text-gray-400 font-bold text-[9px] uppercase">
                      <th className="p-3">Workflow Transition</th>
                      <th className="p-3">Evaluation Remarks</th>
                      <th className="p-3">Changed At</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 text-gray-600">
                    {candidate.stageHistory.map((history, idx) => (
                      <tr key={idx} className="hover:bg-gray-50/50 transition-colors">
                        <td className="p-3 whitespace-nowrap">
                          <div className="flex items-center gap-1">
                            {history.fromStage && (
                              <>
                                <span className="bg-gray-100 px-2 py-0.5 rounded capitalize font-semibold text-gray-700">{history.fromStage}</span>
                                <ChevronRight className="h-3 w-3 text-gray-400" />
                              </>
                            )}
                            <span className="bg-red-50 text-primary px-2 py-0.5 rounded capitalize font-bold">{history.toStage}</span>
                          </div>
                        </td>
                        <td className="p-3 italic">
                          {history.remarks ? `"${history.remarks}"` : 'No custom remarks recorded.'}
                        </td>
                        <td className="p-3 text-[10px] text-gray-400 whitespace-nowrap">
                          {formatDateTime(history.changedAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-4 bg-gray-50 rounded-lg text-center text-xs text-gray-400 italic">
                No active sequential pipeline history logs found.
              </div>
            )}
          </div>

          {/* Evaluations & Interview rounds log */}
          <div className="bg-white border border-gray-100 rounded-xl shadow-sm p-5 space-y-4">
            <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-wider border-b pb-2 flex items-center gap-1.5">
              <ShieldCheck className="h-4 w-4 text-gray-400" /> Evaluation Cycles & Interview Rounds
            </h3>

            {candidate.interviews?.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {candidate.interviews.map((round) => (
                  <div key={round._id} className="border rounded-xl p-4 bg-gray-50/50 space-y-3 shadow-xs">
                    <div className="flex justify-between items-center border-b pb-2">
                      <div>
                        <span className="text-[9px] uppercase tracking-wider text-primary font-bold">ROUND {round.round}</span>
                        <h4 className="font-bold text-gray-800 text-xs mt-0.5">{round.roundName}</h4>
                      </div>
                      <span className="bg-white border px-2 py-0.5 rounded text-[10px] font-bold text-gray-600 capitalize">
                        {round.status}
                      </span>
                    </div>

                    <div className="text-[11px] text-gray-500 space-y-1.5">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-3.5 w-3.5 text-gray-400" />
                        <span>{formatDateTime(round.scheduledAt)} ({round.durationMinutes} min)</span>
                      </div>
                      {round.meetingLink && (
                        <div className="flex items-center gap-2">
                          <Video className="h-3.5 w-3.5 text-green-500" />
                          <a href={round.meetingLink} target="_blank" rel="noreferrer" className="text-blue-600 font-semibold hover:underline">Join Meeting Link</a>
                        </div>
                      )}
                    </div>

                    {/* Interview feedback logs */}
                    {round.feedback?.length > 0 && (
                      <div className="border-t pt-2 space-y-2">
                        <span className="text-[9px] font-bold text-gray-400 uppercase block">Registered Feedback</span>
                        {round.feedback.map((fb, fidx) => (
                          <div key={fidx} className="bg-white border rounded p-2.5 text-[11px] space-y-1.5 shadow-xs">
                            <div className="flex justify-between items-center font-bold text-gray-800">
                              <span>Interviewer Score</span>
                              <span className="text-amber-500">★ {fb.rating}/5</span>
                            </div>
                            <p className="text-gray-500 italic">"{fb.comments}"</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-gray-400 italic bg-gray-50 p-4 rounded-lg text-center">No interview evaluations logged for this candidate.</p>
            )}
          </div>

        </div>

        {/* Right Side: Candidate File, CTC Parameters and Candidate Notes (Takes 1/3 width) */}
        <div className="space-y-6">
          
          {/* Candidate response callout */}
          {offer.status === 'accepted' && (
            <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-r-xl space-y-1.5 shadow-sm">
              <span className="text-[9px] font-extrabold uppercase text-green-600 tracking-wider">Candidate Response</span>
              <p className="text-xs text-green-800 font-semibold italic">"{offer.candidateResponseNote || 'Accepted without response note.'}"</p>
              <div className="text-[9px] text-green-500 font-bold">
                Responded At: {formatDateTime(offer.respondedAt)}
              </div>
            </div>
          )}

          {offer.status === 'rejected' && (
            <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-xl space-y-1.5 shadow-sm">
              <span className="text-[9px] font-extrabold uppercase text-red-600 tracking-wider">Candidate Response</span>
              <p className="text-xs text-red-800 font-semibold italic">"{offer.candidateResponseNote || 'Declined without response note.'}"</p>
              <div className="text-[9px] text-red-500 font-bold">
                Responded At: {formatDateTime(offer.respondedAt)}
              </div>
            </div>
          )}

          {/* Profile Card */}
          <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm space-y-5">
            <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-wider border-b pb-2 flex items-center gap-1.5">
              <User className="h-4 w-4" /> Candidate Profile Summary
            </h3>

            <div className="space-y-3.5 text-xs text-gray-600">
              <div className="flex items-center gap-3">
                <Mail className="h-4 w-4 text-gray-400" />
                <span>{candidate.email}</span>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="h-4 w-4 text-gray-400" />
                <span>{candidate.mobile || 'N/A'}</span>
              </div>
              <div className="flex items-center gap-3">
                <MapPin className="h-4 w-4 text-gray-400" />
                <span>{candidate.currentLocation || 'N/A'}</span>
              </div>
              <div className="flex items-center gap-3">
                <Award className="h-4 w-4 text-gray-400" />
                <span>{candidate.totalExperienceYears} Years Total Exp</span>
              </div>
              <div className="flex items-center gap-3">
                <Building className="h-4 w-4 text-gray-400" />
                <span>Current Co: {candidate.currentCompany || 'N/A'}</span>
              </div>
            </div>

            {/* Resume Upload Document attachment */}
            {candidate.documents?.length > 0 && (
              <div className="border-t pt-4 space-y-2">
                <span className="text-[10px] font-bold text-gray-400 uppercase block mb-1">Submitted Credentials</span>
                {candidate.documents.map((doc, idx) => (
                  <a 
                    key={idx} 
                    href={doc.fileUrl} 
                    target="_blank" 
                    rel="noreferrer" 
                    className="flex items-center gap-2 p-2.5 bg-gray-50 hover:bg-red-50/50 text-gray-700 rounded-lg border text-xs transition-all"
                  >
                    <FileText className="h-4 w-4 text-red-500" />
                    <span className="truncate flex-1 font-semibold">{doc.fileName || 'Resume Attachment'}</span>
                    <ExternalLink className="h-3 w-3 text-gray-400" />
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Job details and managers */}
          <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm space-y-5">
            <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-wider border-b pb-2 flex items-center gap-1.5">
              <Building className="h-4 w-4" /> Position Parameters
            </h3>

            <div className="space-y-4 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <span className="text-[10px] text-gray-400 block font-semibold">Position Offered</span>
                  <span className="font-bold text-gray-800">{roleName}</span>
                </div>
                <div>
                  <span className="text-[10px] text-gray-400 block font-semibold">Department</span>
                  <span className="font-bold text-gray-800">{deptName}</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 pt-2 border-t border-dashed">
                <div>
                  <span className="text-[10px] text-gray-400 block font-semibold">Work Mode</span>
                  <span className="font-bold text-gray-800 capitalize">{offer.employmentType?.replace('_', ' ')}</span>
                </div>
                <div>
                  <span className="text-[10px] text-gray-400 block font-semibold">Location Type</span>
                  <span className="font-bold text-gray-800">{offer.workLocation || 'N/A'}</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 pt-2 border-t border-dashed">
                <div>
                  <span className="text-[10px] text-gray-400 block font-semibold">Probation Period</span>
                  <span className="font-bold text-gray-800">{offer.probationPeriodMonths} Months</span>
                </div>
                <div>
                  <span className="text-[10px] text-gray-400 block font-semibold">Target Joining Date</span>
                  <span className="font-bold text-gray-800">{formatDate(offer.joiningDate)}</span>
                </div>
              </div>
            </div>
          </div>

        </div>

      </div>

    </div>
  );
};

export default OfferDetailView;